import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { isDesktopBreakpoint } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    this.#observeGridVisibility();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#gridObserver?.disconnect();
  }

  /**
   * The grid layout (desktop) has no slide concept of its own - the underlying slideshow
   * is what dispatches selection (from a thumbnail/dot click), but on desktop it's hidden
   * and its own scroller isn't what the visitor sees, so scroll the matching grid item
   * into view instead. On mobile the slideshow itself is what's visible, and it already
   * scrolled its own slide into view as part of dispatching this event - nothing more to do.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (!event.detail.userInitiated || !isDesktopBreakpoint()) return;

    const target = this.media?.[event.detail.index];
    if (!target) return;

    scrollIntoView(target, { behavior: 'smooth', block: 'center' });
  };

  /** @type {IntersectionObserver | null} */
  #gridObserver = null;

  /** @type {Map<Element, number>} */
  #gridVisibility = new Map();

  /**
   * Keeps the desktop thumbnail/dot selection in sync with whichever grid image is most
   * visible while the page scrolls - the grid has no "current slide" of its own, so this
   * is the closest equivalent for it.
   */
  #observeGridVisibility() {
    const items = this.media;
    if (!items?.length) return;

    this.#gridObserver = new IntersectionObserver(this.#handleGridIntersection, {
      threshold: Array.from({ length: 21 }, (_, i) => i / 20),
    });

    for (const item of items) this.#gridObserver.observe(item);
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleGridIntersection = (entries) => {
    for (const entry of entries) {
      this.#gridVisibility.set(entry.target, entry.intersectionRatio);
    }

    let mostVisibleTarget = null;
    let highestRatio = 0;

    for (const [target, ratio] of this.#gridVisibility) {
      if (ratio > highestRatio) {
        highestRatio = ratio;
        mostVisibleTarget = target;
      }
    }

    if (!mostVisibleTarget) return;

    const items = this.media;
    const index = items?.indexOf(/** @type {HTMLElement} */ (mostVisibleTarget));
    if (index == null || index === -1) return;

    const thumbnails = this.slideshow?.refs.thumbnails;
    const dots = this.slideshow?.refs.dots;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === index}`));
    }
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
